import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { PageContainer } from '../../theme/primitives';

const I = {
  Home: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  Refresh: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  ),
  WifiOff: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
      <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
      <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
      <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
      <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
      <line x1="12" y1="20" x2="12.01" y2="20" />
    </svg>
  ),
};

function ActionBtn({ action, primary }) {
  if (!action) return null;
  const cls = primary
    ? `
      group inline-flex h-11 items-center justify-center gap-1.5 rounded-lg px-5
      text-[12px] font-bold uppercase tracking-[0.1em]
      bg-amber text-ink
      transition-[background-color,box-shadow,transform] duration-200 ease-out-soft
      hover:bg-amber-light
      hover:shadow-[0_0_0_3px_rgba(251,191,36,0.28),0_8px_22px_-8px_rgba(251,191,36,0.5)]
      active:scale-[0.98]
    `
    : `
      inline-flex h-11 items-center justify-center gap-1.5 rounded-lg px-5
      text-[12px] font-bold uppercase tracking-[0.08em]
      border border-amber/40 text-amber
      transition-[background-color,border-color] duration-200
      hover:bg-amber/10 hover:border-amber/60
    `;
  if (action.to) {
    return <RouterLink to={action.to} className={cls}>{action.icon}<span>{action.label}</span></RouterLink>;
  }
  return <button type="button" onClick={action.onClick} className={cls}>{action.icon}<span>{action.label}</span></button>;
}

export const ErrorPage = ({
  code,
  title,
  subtitle,
  icon,
  primaryAction = { label: 'Back to home', to: '/', icon: <I.Home className="h-4 w-4" /> },
  secondaryAction,
}) => (
  <PageContainer maxWidth="sm">
    <div className="relative flex min-h-[calc(100vh-220px)] items-center justify-center py-10 sm:py-14">
      {/* ambient amber glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        style={{
          width: 520,
          height: 420,
          background: 'radial-gradient(ellipse, rgba(251,191,36,0.10), transparent 65%)',
        }}
      />
      <div className="
        relative w-full max-w-[480px] overflow-hidden
        rounded-2xl border border-amber/24 bg-surface-paper
        px-6 py-10 sm:px-8 sm:py-12
        text-center
        shadow-card
      ">
        <span aria-hidden className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-amber/60 to-transparent" />
        {icon && (
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber/12 text-amber border border-amber/24">
            {icon}
          </div>
        )}
        {code && (
          <div
            aria-hidden
            className="mb-2 text-[64px] sm:text-[96px] font-extrabold leading-none tracking-[-0.04em] text-amber"
            style={{ textShadow: '0 0 30px rgba(251,191,36,0.25)' }}
          >
            {code}
          </div>
        )}
        <h1 className="text-[20px] sm:text-[24px] font-bold tracking-[-0.018em] text-fg-primary">
          {title}
        </h1>
        <div aria-hidden className="mx-auto my-3 h-px w-20 rounded-full bg-gradient-to-r from-transparent via-amber/60 to-transparent" />
        <p className="mx-auto max-w-[360px] text-[13.5px] leading-[1.6] text-fg-secondary">
          {subtitle}
        </p>

        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          <ActionBtn action={primaryAction} primary />
          <ActionBtn action={secondaryAction} />
        </div>
      </div>
    </div>
  </PageContainer>
);

export const NotFound = () => (
  <ErrorPage
    code="404"
    title="Page not found"
    subtitle="The page you're looking for doesn't exist or may have been moved."
    secondaryAction={{ label: 'Go back', onClick: () => window.history.back(), icon: <I.Refresh className="h-4 w-4" /> }}
  />
);

export const ServerError = () => (
  <ErrorPage
    code="500"
    title="Something went wrong"
    subtitle="We're working on fixing it. Please try again in a moment."
    primaryAction={{ label: 'Try again', onClick: () => window.location.reload(), icon: <I.Refresh className="h-4 w-4" /> }}
    secondaryAction={{ label: 'Back to home', to: '/', icon: <I.Home className="h-4 w-4" /> }}
  />
);

export const Forbidden = () => (
  <ErrorPage
    code="403"
    title="Access denied"
    subtitle="You don't have permission to view this page."
  />
);

export const Offline = () => (
  <ErrorPage
    icon={<I.WifiOff className="h-7 w-7" />}
    title="You're offline"
    subtitle="Check your internet connection and try again. Some ESAY features need an active connection."
    primaryAction={{ label: 'Try again', onClick: () => window.location.reload(), icon: <I.Refresh className="h-4 w-4" /> }}
    secondaryAction={{ label: 'Back to home', to: '/', icon: <I.Home className="h-4 w-4" /> }}
  />
);
